import { useCallback } from 'react';
import {
  Stack, ListItem, ListItemAvatar, ListItemText,
  Link as MuiLink,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Profile } from '@client/components/Main/Profiles/Profile';
import { SiteContainer } from '@client/components/common/SiteContainer';
import { Modal } from '@client/components/globals/Modal';
import { ProfileTypes } from '@sda/enums';
import { getNormalizedProfile } from '@sda/utils/meta';
import { useProfileLink } from '@client/components/Main/Profiles/useProfileLink.hook';
import { ElevatedCard } from '@client/components/common/BaseCard/ElevatedCard';
import { MediaEmbed } from '@client/components/common/MediaEmbed';
import { FeaturedReview } from '@client/components/common/Review/FeaturedReview';
import { getExternalUrl } from '@sda/utils/url';
import { Avatar } from '@client/components/common/Avatar';
import { Description } from '@client/components/common/Description';
import type { NormalizedPublicProfile } from '@sda/interfaces/show';

const useStyles = makeStyles()((theme) => ({
  websiteUrlLink: {
    color: `${theme.palette.primary.main} !important`,
  },
  listItemProfile: {
    display: 'inline-flex',
    paddingBottom: 0,
    paddingTop: 0,
    width: 'auto',

    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
}));

interface ProfileItemProps {
  readonly preview?: boolean;
}

export const ProfileItem: React.FC<ProfileItemProps & NormalizedPublicProfile> = ({ preview = false, ...profile }) => {
  const { classes } = useStyles();

  const handleProfileButtonClick = useCallback(() => {
    const isArtistProfile = profile.profileType === ProfileTypes.ACT;

    Modal.show({
      title: isArtistProfile ? 'View Artist Profile' : 'View Host Profile',
      fullScreen: true,
      maxWidth: 'narrow',
      buttons: [],
      scroll: 'body',
      body: (
        <SiteContainer
          maxWidth="narrow"
          disableGutters
        >
          <Profile
            {...getNormalizedProfile(profile.profileType, profile)}
          />
        </SiteContainer>
      ),
    });
  }, [profile]);

  const profileLinkProps = useProfileLink(profile.profileType, profile, handleProfileButtonClick);

  const bio = profile.bioHTML || profile.description;

  return (
    <ElevatedCard>
      <Stack spacing={2}>
        <ListItem
          aria-label={`view profile ${profile.name}`}
          disabled={Boolean(preview)}
          className={classes.listItemProfile}
          button
          disableGutters
          {...profileLinkProps}
        >
          <ListItemAvatar>
            <Avatar
              size="small"
              images={profile.publicImageUrl}
              fallback={null}
              alt=""
            />
          </ListItemAvatar>

          <ListItemText
            primary={profile.name}
            primaryTypographyProps={{
              variant: 'subtitle2',
              color: 'textSecondary',
              className: 'break-word',
            }}
          />
        </ListItem>

        {Boolean(bio) && (
          <Description variant="body1">
            {bio}
          </Description>
        )}

        {Boolean(profile.websiteUrl) && (
          <MuiLink
            id={`sda-show-${profile.profileType}-website-link`}
            href={getExternalUrl(profile.websiteUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.websiteUrlLink}
          >
            Website
          </MuiLink>
        )}

        {Boolean(profile.featuredReviewId) && (
          <FeaturedReview
            disableProfileClick={preview}
            reviewId={profile.featuredReviewId}
          />
        )}

        <MediaEmbed
          mediaType="video"
          {...profile}
        />

        <MediaEmbed
          mediaType="audio"
          {...profile}
        />
      </Stack>
    </ElevatedCard>
  );
};
